/**
 * Shared HTTP Client
 *
 * Single Axios instance for the HRMS backend, used by every feature's service
 * layer. Handles cross-cutting HTTP concerns only (base URL, auth headers,
 * tenant/timezone headers, timeout, 401 handling, Sentry reporting, error
 * normalization) — no business/domain logic belongs here.
 *
 * Consolidated from the former src/utils/apiClient.ts and
 * src/features/tickets/api/ticketAxiosClient.ts, which both talked to the
 * same backend with the same auth mechanism.
 */

import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { STORAGE_KEYS } from '@/config/appConfig';
import { getApiBaseUrl } from '@/config/env';
import { getToken, setToken, removeToken } from '@/services/authService';

// Export API Base URL for use in other services.
export const API_BASE_URL = getApiBaseUrl();

/**
 * Get the in-memory access token. Held in authService, never in web storage.
 */
function getAuthToken(): string | null {
  return getToken();
}

/**
 * Get tenant ID from the session user data
 */
function getTenantId(): string | null {
  try {
    const userJson = sessionStorage.getItem(STORAGE_KEYS.user);
    if (userJson) {
      const user = JSON.parse(userJson);
      return user.tenantId || null;
    }
  } catch (error) {
    console.error('Failed to get tenant ID:', error);
  }
  return null;
}

/**
 * Get user's timezone from localStorage (set by TimezoneContext)
 */
function getUserTimezone(): string {
  try {
    const timezone = localStorage.getItem('user_timezone');
    if (timezone) return timezone;

    // Fallback to browser's timezone
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (error) {
    console.error('Failed to get timezone:', error);
    return 'UTC';
  }
}

/**
 * Check if user is authenticated (has valid token)
 */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

/**
 * Maps an HTTP status code to a friendly, user-facing message.
 * Used only as a last resort when the response body carries no usable message.
 * Never exposes the raw status code to the user.
 */
function friendlyMessageForStatus(status?: number): string {
  switch (status) {
    case 400:
    case 422:
      return 'Some of the information you entered is invalid. Please review and try again.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return 'The requested item could not be found.';
    case 409:
      return 'This action conflicts with the current state. Please refresh and try again.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 503:
      return 'The service is temporarily unavailable. Please try again shortly.';
    default:
      if (status && status >= 500) return 'Something went wrong on our end. Please try again later.';
      if (status && status >= 400) return 'We could not process your request. Please try again.';
      return 'Something went wrong. Please try again.';
  }
}

/**
 * Extracts a clean, human-readable message from an Axios error.
 *
 * Handles every common backend error shape:
 *  - Custom envelope:           { error: "..." } / { message: "..." }
 *  - ProblemDetails:            { detail: "..." } / { title: "..." }
 *  - ValidationProblemDetails:  { errors: { Field: ["msg", ...] } }
 *  - Plain string body
 *
 * Guarantees: NEVER returns Axios's "Request failed with status code XXX"
 * or any raw status code. Falls back to a friendly status-based message.
 */
export function extractApiErrorMessage(error: AxiosError): string {
  // No response at all — network failure / server unreachable.
  if (!error.response) {
    return 'Unable to reach the server. Please check your connection and try again.';
  }

  const data = error.response.data as any;

  if (data) {
    if (typeof data === 'string' && data.trim()) {
      return data.trim();
    }
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim();
    }
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message.trim();
    }
    if (typeof data.detail === 'string' && data.detail.trim()) {
      return data.detail.trim();
    }
    // ASP.NET ValidationProblemDetails: { errors: { Field: ["msg", ...] } }
    if (data.errors && typeof data.errors === 'object') {
      const messages = Object.values(data.errors)
        .flat()
        .filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
      if (messages.length > 0) {
        return messages.join(' ');
      }
    }
    // ProblemDetails title as a last resort from the body.
    if (typeof data.title === 'string' && data.title.trim()) {
      return data.title.trim();
    }
  }

  return friendlyMessageForStatus(error.response.status);
}

/**
 * Session is unrecoverable — clear local state and send the user to login.
 * Only called when the refresh itself fails, never on a bare 401.
 */
function handleUnauthorized(): void {
  removeToken();

  // Avoid a redirect loop when the failing request *is* the login page's own call.
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

// Create the shared Axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  // Sends the HttpOnly refresh-token cookie on every request. Harmless on
  // non-auth endpoints — the cookie's Path is scoped to /api/v1/auth, so the
  // browser won't even attach it elsewhere.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * The in-flight refresh, or null. This single shared promise is what makes N concurrent
 * 401s produce exactly one refresh call: whoever arrives first starts it, everyone else
 * awaits the same promise instead of starting their own.
 *
 * Without it, ten parallel requests hitting an expired token would fire ten rotations —
 * and since each rotation invalidates the previous token, nine of them would be treated
 * as token reuse and the user's whole session would be revoked as an attack.
 */
let refreshInFlight: Promise<string> | null = null;

/**
 * Exchanges the refresh cookie for a new access token.
 *
 * Uses a bare axios call, not apiClient: going through the instance would re-enter this
 * same interceptor on failure and recurse.
 */
async function refreshAccessToken(): Promise<string> {
  const response = await axios.post<{ token: string; expiresInSeconds: number }>(
    `${API_BASE_URL}/auth/refresh`,
    null,
    {
      withCredentials: true,
      // Non-simple header: forces a CORS preflight, which is what stops an untrusted
      // origin from driving this endpoint. The server rejects the request without it.
      headers: { 'X-Refresh-Request': '1' },
    }
  );

  const token = response.data?.token;
  if (!token) throw new Error('Refresh returned no token');

  setToken(token);
  return token;
}

/**
 * Refresh, collapsing concurrent callers onto one request.
 *
 * Exported so other axios instances (the ticket module has its own) share this single
 * promise. If each client kept its own, two expiring in parallel would fire two
 * rotations — and the second would be seen as token reuse and kill the whole session.
 */
export function refreshOnce(): Promise<string> {
  refreshInFlight ??= refreshAccessToken().finally(() => {
    // Cleared in `finally` so a failed refresh doesn't poison every later attempt with a
    // permanently rejected promise.
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/** Exposed for app boot: rehydrate the in-memory token after a page load. */
export async function restoreSession(): Promise<boolean> {
  try {
    await refreshOnce();
    return true;
  } catch {
    return false;
  }
}

// Request Interceptor: Attach Token, Tenant ID, and Timezone
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const tenantId = getTenantId();
    const timezone = getUserTimezone();

    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (tenantId && config.headers) {
      config.headers['X-Tenant-Id'] = tenantId;
    }

    if (timezone && config.headers) {
      config.headers['X-Timezone'] = timezone;
    }

    // Do NOT set Content-Type for FormData bodies — let Axios/the browser set
    // the multipart boundary automatically. Ported from the former
    // ticketAxiosClient.ts so raw FormData POSTs (e.g. ticket attachments)
    // keep working once routed through this shared instance.
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Marks a request that has already gone through one refresh+retry cycle, so a
// second 401 on the same request logs out instead of looping forever.
declare module 'axios' {
  export interface AxiosRequestConfig {
    _retry?: boolean;
  }
}

// Response Interceptor: Handle Global Errors like 401 Unauthorized
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

    // Never attempt a refresh in response to the refresh/login calls
    // themselves — that would recurse or refresh a session that never existed.
    const isAuthEndpoint =
      original?.url?.includes('/auth/refresh') ||
      original?.url?.includes('/auth/login') ||
      original?.url?.includes('/auth/m365-login');

    // A 401 now means "access token expired", not "session over". Refresh once (collapsing
    // concurrent callers via refreshOnce) and replay. _retry guards against a loop: if the
    // replayed request 401s again, the problem is not an expired token and another refresh
    // will not help.
    if (error.response?.status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;

      try {
        const token = await refreshOnce();
        original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
        return apiClient.request(original); // retry the ORIGINAL request, once
      } catch {
        handleUnauthorized();
        return Promise.reject(new Error('Session expired. Please login again.'));
      }
    }

    if (error.response && error.response.status === 401) {
      handleUnauthorized();
      return Promise.reject(new Error('Session expired. Please login again.'));
    }

    // Request timed out
    if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
      Sentry.captureException(error, { tags: { type: 'api_timeout', url: error.config?.url } });
      return Promise.reject(new Error('Request timed out. Please try again.'));
    }

    // Report non-401 API errors to Sentry
    Sentry.captureException(error, {
      tags: {
        type: 'api_error',
        status: String(error.response?.status ?? 'network'),
      },
      extra: {
        url: error.config?.url,
        method: error.config?.method,
      },
    });

    // Always surface a clean, human-readable message — never Axios's
    // "Request failed with status code XXX" or a raw status code. The status is carried
    // alongside it for the few callers that must tell "not found" apart from "server down".
    const apiError = new Error(extractApiErrorMessage(error)) as Error & { status?: number };
    apiError.status = error.response?.status;
    return Promise.reject(apiError);
  }
);

/**
 * Legacy authenticatedFetch wrapper to keep backwards compatibility
 * for any service that specifically uses authenticatedFetch instead of apiGet/apiPost
 */
export async function authenticatedFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  try {
    const method = options.method || 'GET';
    const isFormData = options.body instanceof FormData;

    // Convert RequestInit to AxiosRequestConfig
    const config: AxiosRequestConfig = {
      method: method,
      url: endpoint,
      headers: (options.headers as any) || {},
      data: options.body,
    };

    // Axios sets boundary automatically for FormData if Content-Type is not explicitly set
    if (isFormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    const response = await apiClient.request(config);

    // Create a mock Fetch Response to maintain interface contract
    return {
      ok: true,
      status: response.status,
      statusText: response.statusText,
      json: async () => response.data,
      headers: {
        get: (name: string) => response.headers[name.toLowerCase()]
      } as any
    } as Response;
  } catch (error: any) {
    if (error.response) {
       // Re-throw handled error if possible, otherwise mimic fetch behavior
       const mockResponse = {
         ok: false,
         status: error.response.status,
         statusText: error.response.statusText,
         json: async () => error.response.data,
         headers: {
            get: (name: string) => error.response.headers[name.toLowerCase()]
         } as any
       } as Response;
       return mockResponse;
    }
    throw error;
  }
}

/**
 * Authenticated GET request
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
  const response = await apiClient.get<T>(endpoint);
  return response.data;
}

/**
 * Authenticated POST request. `config` is optional per-request extras (e.g. an
 * Idempotency-Key header); the 401-refresh retry replays it unchanged.
 */
export async function apiPost<T>(
  endpoint: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<T>(endpoint, data, config);
  return response.data;
}

/**
 * Authenticated PUT request
 */
export async function apiPut<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await apiClient.put<T>(endpoint, data);
  return response.data;
}

/**
 * Authenticated DELETE request
 */
export async function apiDelete<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await apiClient.delete<T>(endpoint, { data });
  return response.data;
}

/**
 * Authenticated GET that returns a Blob (for file downloads)
 */
export async function apiGetBlob(endpoint: string): Promise<Blob> {
  const response = await apiClient.get<Blob>(endpoint, { responseType: 'blob' });
  return response.data;
}

/**
 * Authenticated POST with FormData (for file uploads)
 */
export async function apiPostFormData<T>(endpoint: string, formData: FormData): Promise<T> {
  const response = await apiClient.post<T>(endpoint, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}


export async function apiPatch<T>(
  endpoint: string,
  data?: unknown
): Promise<T> {
  const response = await authenticatedFetch(
    endpoint,
    {
      method: "PATCH",
      body: data
        ? JSON.stringify(data)
        : undefined,
    }
  );

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({
        error: "Request failed",
      }));

    throw new Error(
      errorData.error ||
      `HTTP ${response.status}: ${response.statusText}`
    );
  }

  // Handle 204 No Content
  if (
    response.status === 204 ||
    response.headers.get("content-length") ===
    "0"
  ) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Raw Axios instance, exported for features (e.g. Tickets) that need direct
 * access/response typing rather than the apiGet/apiPost helpers above.
 */
export default apiClient;
